import React, { useState, useEffect } from 'react'

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;0,900;1,400&family=Source+Serif+4:ital,opsz,wght@0,8..60,300;0,8..60,400;0,8..60,600;1,8..60,300&family=Space+Mono:wght@400;700&display=swap');

  * { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --ink: #0f0e0c;
    --paper: #f5f0e8;
    --paper-dark: #ede7d6;
    --steel: #2c3e50;
    --steel-mid: #4a6274;
    --accent: #b5451b;
    --accent-light: #e8623a;
    --gold: #c9943a;
    --gold-light: #f0c96e;
    --rule: #d4c9b0;
  }

  .article-root {
    background: var(--paper);
    min-height: 100vh;
    font-family: 'Source Serif 4', Georgia, serif;
    color: var(--ink);
    position: relative;
    overflow-x: hidden;
  }

  /* HEADER */
  .article-header {
    background: var(--steel);
    padding: 0;
    position: relative;
    overflow: hidden;
  }

  .header-noise {
    position: absolute;
    inset: 0;
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.06'/%3E%3C/svg%3E");
    opacity: 0.4;
    pointer-events: none;
  }

  .header-stripe {
    height: 4px;
    background: linear-gradient(90deg, var(--accent) 0%, var(--gold) 50%, var(--accent) 100%);
  }

  .header-inner {
    max-width: 900px;
    margin: 0 auto;
    padding: 48px 40px 40px;
    position: relative;
    z-index: 1;
  }

  .journal-label {
    font-family: 'Space Mono', monospace;
    font-size: 10px;
    letter-spacing: 0.3em;
    text-transform: uppercase;
    color: var(--gold-light);
    opacity: 0.8;
    margin-bottom: 20px;
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .journal-label::before,
  .journal-label::after {
    content: '';
    flex: 1;
    height: 1px;
    background: var(--gold);
    opacity: 0.3;
  }

  .article-title {
    font-family: 'Playfair Display', serif;
    font-size: clamp(24px, 4vw, 44px);
    font-weight: 900;
    line-height: 1.15;
    color: #fff;
    margin-bottom: 28px;
    letter-spacing: -0.02em;
  }

  .article-title .accent-word {
    color: var(--gold-light);
    font-style: italic;
  }

  .author-block {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 16px 0;
    border-top: 1px solid rgba(201,148,58,0.3);
  }

  .author-avatar {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    background: linear-gradient(135deg, var(--accent), var(--gold));
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'Playfair Display', serif;
    font-size: 18px;
    font-weight: 700;
    color: #fff;
    flex-shrink: 0;
  }

  .author-info {
    display: flex;
    flex-direction: column;
  }

  .author-name {
    font-family: 'Playfair Display', serif;
    font-size: 15px;
    font-weight: 700;
    font-style: italic;
    color: #fff;
  }

  .author-role {
    font-family: 'Space Mono', monospace;
    font-size: 9px;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: var(--gold-light);
    opacity: 0.7;
    margin-top: 2px;
  }

  /* HERO IMAGE */
  .hero-image-section {
    position: relative;
    width: 100%;
    // height: 340px;
    overflow: hidden;
    background: var(--steel);
  }

  .hero-image-section img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    opacity: 0.75;
    filter: sepia(20%) contrast(1.1);
  }

  .hero-caption {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 20px 40px 16px;
    background: linear-gradient(to top, rgba(15,14,12,0.85) 0%, transparent 100%);
  }

  .hero-caption-text {
    font-family: 'Space Mono', monospace;
    font-size: 10px;
    letter-spacing: 0.15em;
    text-transform: uppercase;
    color: var(--gold-light);
    opacity: 0.9;
  }

  /* MAIN BODY */
  .article-body {
    max-width: 900px;
    margin: 0 auto;
    padding: 0 40px 80px;
  }

  /* ABSTRACT */
  .abstract-block {
    margin: 48px 0 40px;
    padding: 32px 36px;
    background: var(--paper-dark);
    border-left: 4px solid var(--accent);
    position: relative;
    box-shadow: 4px 4px 0 rgba(181,69,27,0.08);
  }

  .abstract-label {
    font-family: 'Space Mono', monospace;
    font-size: 9px;
    letter-spacing: 0.35em;
    text-transform: uppercase;
    color: var(--accent);
    margin-bottom: 14px;
    font-weight: 700;
  }

  .abstract-text {
    font-size: 15px;
    line-height: 1.8;
    color: var(--steel);
    font-style: italic;
    font-weight: 300;
  }

  /* SECTION */
  .section {
    margin-bottom: 40px;
    padding-bottom: 40px;
    border-bottom: 1px solid var(--rule);
    animation: fadeUp 0.5s ease both;
  }

  .section:last-child {
    border-bottom: none;
  }

  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(16px); }
    to { opacity: 1; transform: translateY(0); }
  }

  .section-number {
    font-family: 'Space Mono', monospace;
    font-size: 11px;
    letter-spacing: 0.15em;
    color: var(--gold);
    font-weight: 700;
    margin-bottom: 6px;
  }

  .section-title {
    font-family: 'Playfair Display', serif;
    font-size: clamp(18px, 2.5vw, 26px);
    font-weight: 700;
    color: var(--steel);
    margin-bottom: 18px;
    line-height: 1.25;
    border-bottom: 2px solid var(--rule);
    padding-bottom: 10px;
  }

  .section-body {
    font-size: 16px;
    line-height: 1.85;
    color: #2a2a2a;
    font-weight: 300;
  }

  .section-body p {
    margin-bottom: 14px;
  }

  .section-body strong {
    color: var(--steel);
    font-weight: 600;
  }

  /* UNDERLINED KEY PHRASE */
  .key-phrase {
    text-decoration: underline;
    text-decoration-color: var(--gold);
    text-underline-offset: 3px;
    font-weight: 600;
    color: var(--steel);
  }

  /* BULLET LIST */
  .bullet-list {
    list-style: none;
    margin: 14px 0 14px 8px;
    padding: 0;
  }

  .bullet-list li {
    padding: 6px 0 6px 24px;
    position: relative;
    font-size: 15.5px;
    line-height: 1.7;
    color: #2a2a2a;
    font-weight: 300;
  }

  .bullet-list li::before {
    content: '';
    position: absolute;
    left: 0;
    top: 15px;
    width: 10px;
    height: 2px;
    background: var(--accent);
  }

  /* STAT CALLOUT */
  .stat-callout {
    margin: 32px 0;
    padding: 24px 28px;
    background: var(--steel);
    color: #fff;
    display: flex;
    align-items: center;
    gap: 24px;
    position: relative;
    overflow: hidden;
  }

  .stat-callout::before {
    content: '';
    position: absolute;
    right: -20px;
    top: -20px;
    width: 120px;
    height: 120px;
    border-radius: 50%;
    background: rgba(201,148,58,0.12);
  }

  .stat-num {
    font-family: 'Playfair Display', serif;
    font-size: 42px;
    font-weight: 900;
    color: var(--gold-light);
    line-height: 1;
    flex-shrink: 0;
  }

  .stat-desc {
    font-family: 'Source Serif 4', serif;
    font-size: 14px;
    line-height: 1.6;
    color: rgba(255,255,255,0.85);
    font-style: italic;
  }

  /* PULL QUOTE */
  .pull-quote {
    margin: 36px -12px;
    padding: 28px 32px;
    border-top: 3px solid var(--gold);
    border-bottom: 3px solid var(--gold);
    text-align: center;
  }

  .pull-quote-text {
    font-family: 'Playfair Display', serif;
    font-size: clamp(16px, 2vw, 21px);
    font-style: italic;
    line-height: 1.6;
    color: var(--steel);
  }

  /* TABLE OF CONTENTS */
  .toc-sidebar {
    background: var(--paper-dark);
    border: 1px solid var(--rule);
    padding: 24px 28px;
    margin: 0 0 44px;
  }

  .toc-title {
    font-family: 'Space Mono', monospace;
    font-size: 10px;
    letter-spacing: 0.3em;
    text-transform: uppercase;
    color: var(--steel-mid);
    margin-bottom: 14px;
    font-weight: 700;
  }

  .toc-list {
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .toc-item {
    display: flex;
    align-items: baseline;
    gap: 8px;
    font-size: 13px;
    color: var(--steel);
    cursor: pointer;
    padding: 3px 0;
    transition: color 0.2s;
  }

  .toc-item:hover {
    color: var(--accent);
  }

  .toc-num {
    font-family: 'Space Mono', monospace;
    font-size: 10px;
    color: var(--gold);
    flex-shrink: 0;
  }

  /* FOOTER */
  .article-footer {
    background: var(--steel);
    padding: 32px 40px;
    text-align: center;
  }

  .footer-logo {
    font-family: 'Space Mono', monospace;
    font-size: 10px;
    letter-spacing: 0.3em;
    text-transform: uppercase;
    color: var(--gold-light);
    opacity: 0.6;
  }

  /* RESPONSIVE */
  @media (max-width: 600px) {
    .header-inner, .article-body, .hero-caption, .article-footer { padding-left: 20px; padding-right: 20px; }
    .abstract-block { padding: 22px 20px; }
    .stat-callout { flex-direction: column; gap: 8px; text-align: center; }
    .hero-image-section { height: 220px; }
  }
`

const sections = [
  {
    num: '01',
    title: 'Introduction',
    content: (
      <>
        <p>The steel industry is fundamental to global economic development, serving as a backbone for infrastructure, manufacturing, transportation, and energy systems. However, it is also among the most energy-intensive industrial sectors, accounting for approximately <strong>7–8 percent of global carbon dioxide emissions</strong>. The decarbonization of steel production has therefore become a central priority in global climate policy discussions.</p>
        <p>India has emerged as the <strong>second-largest steel producer in the world</strong>, and the sector continues to expand to support the country's ambitious infrastructure and industrial development goals. Within this landscape, the <strong>secondary steel sector</strong>—comprised of electric induction furnace units, re-rolling mills, and numerous small and medium-sized enterprises—plays a vital role in meeting domestic steel demand.</p>
        <p>Despite its importance, the sector faces significant challenges in transitioning to low-carbon production pathways due to financial constraints, limited access to advanced technologies, and structural fragmentation. Recognizing these challenges, the <strong>AIIFA Sustainable Steel Manufacturers Association</strong> has developed a comprehensive roadmap to guide the secondary steel sector toward sustainable and low-carbon growth.</p>
      </>
    )
  },
  {
    num: '02',
    title: 'Carbon Emissions and Structural Challenges in the Steel Sector',
    content: (
      <>
        <p>The global steel industry traditionally relies on <strong>blast furnace–basic oxygen furnace (BF-BOF) technology</strong>, which depends heavily on coal and coke as primary energy sources. These processes involve high-temperature chemical reactions that generate substantial carbon emissions.</p>
        <p>In contrast, the <strong>secondary steel sector in India primarily utilizes electric induction furnace technology</strong>, which has the potential to significantly reduce emissions when powered by low-carbon electricity. However, the sector is dominated by SMEs that often face barriers in adopting advanced technologies and energy-efficient systems.</p>
        <p><span className="key-phrase">Key challenges include:</span></p>
        <ul className="bullet-list">
          <li>Limited access to capital for technological upgrades</li>
          <li>High electricity tariffs and energy price volatility</li>
          <li>Fragmented supply chains for scrap and raw materials</li>
          <li>Limited availability of climate-aligned financing mechanisms</li>
        </ul>
        <p>Addressing these barriers requires coordinated efforts involving industry associations, policymakers, financial institutions, and research organizations.</p>
      </>
    )
  },
  {
    num: '03',
    title: 'Innovation and Energy Efficiency in Steel Production',
    content: (
      <>
        <p>Technological innovation and energy efficiency improvements form a central pillar of AIIFA's sustainability strategy. Electric induction furnaces already provide a comparatively lower-emission pathway for steel production. However, further improvements can significantly enhance efficiency and reduce environmental impacts. AIIFA encourages the adoption of:</p>
        <ul className="bullet-list">
          <li>Advanced furnace designs with improved thermal efficiency</li>
          <li>Automated process control systems</li>
          <li>Waste heat recovery technologies</li>
          <li>Energy-efficient rolling mills</li>
          <li>Digital monitoring and predictive maintenance systems</li>
        </ul>
        <p>In addition, the integration of <strong>Industry 4.0 technologies</strong>, including Internet of Things (IoT) sensors, real-time monitoring systems, and data-driven analytics, enables steel manufacturers to optimize production processes, minimize energy consumption, and reduce operational inefficiencies.</p>
        <p>These innovations not only reduce emissions but also improve productivity and cost competitiveness.</p>
      </>
    )
  },
  {
    num: '04',
    title: 'Renewable Energy Integration',
    content: (
      <>
        <p>The transition toward renewable energy represents a critical pathway for reducing the carbon footprint of electric-based steel production. AIIFA actively promotes the integration of renewable energy sources such as <strong>solar and wind power</strong> within steel manufacturing operations. The association encourages members to explore multiple procurement models, including:</p>
        <ul className="bullet-list">
          <li>Green power purchase agreements</li>
          <li>Open-access renewable energy procurement</li>
          <li>Captive solar installations</li>
          <li>Hybrid renewable energy systems</li>
        </ul>
        <p>The adoption of renewable energy offers several advantages, including long-term energy cost stability, reduced exposure to fossil fuel price volatility, and significant reductions in carbon emissions. Furthermore, AIIFA advocates for policy reforms that enable broader access to renewable power, including rationalized power tariffs and expanded open-access provisions.</p>
      </>
    )
  },
  {
    num: '05',
    title: 'Research, Technology Development and Industrial Collaboration',
    content: (
      <>
        <p>Bridging the gap between scientific research and industrial implementation is essential for achieving long-term decarbonization. AIIFA collaborates with leading academic and research institutions, including the <strong>Indian Institutes of Technology</strong>, to explore emerging technologies that could transform steel production. Areas of focus include:</p>
        <ul className="bullet-list">
          <li>Hydrogen-based Direct Reduced Iron (H-DRI)</li>
          <li>Carbon Capture, Utilization and Storage (CCUS)</li>
          <li>Digital twin technologies for manufacturing optimization</li>
          <li>Advanced materials and process engineering</li>
        </ul>
        <p>These collaborations facilitate pilot projects, technology demonstrations, and knowledge exchange between industry practitioners and researchers. By supporting innovation ecosystems, AIIFA helps ensure that advanced technologies are adapted to the operational realities of SME-based steel production.</p>
      </>
    )
  },
  {
    num: '06',
    title: 'Policy Advocacy and Institutional Support',
    content: (
      <>
        <p>The transition to low-carbon steel production requires a supportive policy and regulatory environment. AIIFA actively engages with government ministries and regulatory authorities to advocate for policies that promote sustainable industrial development. Key policy priorities include:</p>
        <ul className="bullet-list">
          <li>Fiscal incentives for energy-efficient technologies</li>
          <li>Capital subsidies for modernization of steel plants</li>
          <li>Incentives for renewable energy adoption</li>
          <li>Development of equitable carbon market mechanisms</li>
          <li>Improved access to green and ESG-linked financing</li>
        </ul>
        <p>Such policy interventions can significantly reduce the financial barriers faced by SMEs in adopting sustainable technologies.</p>
      </>
    )
  },
  {
    num: '07',
    title: 'Circular Economy and Resource Efficiency',
    content: (
      <>
        <p>The steel sector is inherently well suited to circular economy practices due to the recyclability of steel. AIIFA strongly promotes <strong>scrap-based steelmaking</strong>, which significantly reduces energy consumption and carbon emissions compared to primary steel production. Strengthening the domestic scrap ecosystem can therefore play a crucial role in advancing sustainability objectives.</p>
        <p>Additionally, AIIFA encourages the beneficial utilization of industrial by-products such as steel slag in sectors including:</p>
        <ul className="bullet-list">
          <li>Cement manufacturing</li>
          <li>Road construction</li>
          <li>Infrastructure development</li>
        </ul>
        <p>These practices contribute to waste reduction, resource conservation, and improved material efficiency. The adoption of <strong>Life Cycle Assessment (LCA)</strong> methodologies further enables steel producers to evaluate environmental impacts throughout the product lifecycle and identify opportunities for sustainability improvements.</p>
      </>
    )
  },
  {
    num: '08',
    title: 'Knowledge Sharing and Capacity Development',
    content: (
      <>
        <p>Industry transformation requires continuous learning and knowledge exchange. AIIFA organizes national conferences, technical seminars, and regional workshops that bring together policymakers, researchers, technology providers, and industry leaders. These platforms facilitate the dissemination of best practices and promote the adoption of innovative technologies across the sector.</p>
        <p>The association also conducts specialized training programs designed to enhance workforce capabilities in areas such as energy management, environmental compliance, and digital manufacturing technologies. By strengthening human capital, AIIFA ensures that sustainability initiatives are supported by skilled professionals capable of implementing change at the operational level.</p>
      </>
    )
  },
  {
    num: '09',
    title: 'Global Alignment and Climate Commitments',
    content: (
      <>
        <p>As global supply chains increasingly prioritize sustainability, alignment with international climate frameworks has become essential for maintaining competitiveness in global markets. AIIFA actively engages with international industry forums and policy dialogues to ensure that the Indian secondary steel sector remains aligned with global sustainability standards and best practices.</p>
        <p>These efforts support India's commitments under the <strong>Paris Climate Agreement</strong> and the <strong>United Nations Sustainable Development Goals</strong>, while also positioning Indian steel producers to meet evolving requirements related to carbon disclosure, sustainable sourcing, and climate risk management.</p>
      </>
    )
  },
  {
    num: '10',
    title: 'Conclusion',
    content: (
      <>
        <p>The transition toward low-carbon steel production represents both a challenge and an opportunity for India's industrial sector. AIIFA's roadmap provides a comprehensive framework for enabling this transition within the country's SME-driven secondary steel ecosystem.</p>
        <p>By integrating technological innovation, renewable energy adoption, policy advocacy, circular economy practices, and workforce development, the association has created a practical pathway for achieving sustainable growth while maintaining industrial competitiveness.</p>
        <p>As global climate commitments intensify and markets increasingly prioritize low-carbon production, initiatives such as AIIFA's roadmap will play a critical role in positioning India's steel industry as a leader in <strong>sustainable and resilient industrial development</strong>.</p>
      </>
    )
  }
]

const tocItems = [
  { num: '01', label: 'Introduction' },
  { num: '02', label: 'Carbon Emissions & Structural Challenges' },
  { num: '03', label: 'Innovation and Energy Efficiency' },
  { num: '04', label: 'Renewable Energy Integration' },
  { num: '05', label: 'Research & Industrial Collaboration' },
  { num: '06', label: 'Policy Advocacy & Institutional Support' },
  { num: '07', label: 'Circular Economy & Resource Efficiency' },
  { num: '08', label: 'Knowledge Sharing & Capacity Development' },
  { num: '09', label: 'Global Alignment & Climate Commitments' },
  { num: '10', label: 'Conclusion' },
]

const Article1 = () => {
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    setLoaded(true)
  }, [])

  const scrollTo = (idx) => {
    const el = document.getElementById(`section-${idx}`)
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  return (
    <div className="article-root">
      <style>{styles}</style>

      {/* HEADER */}
      <header className="article-header">
        <div className="header-noise" />
        <div className="header-stripe" />
        <div className="header-inner">
          <div className="journal-label">AIIFA Research Publication · April 2025 · Sustainable Steel Series</div>
          <h1 className="article-title">
            Decarbonizing India's <span className="accent-word">Secondary Steel</span> Sector: AIIFA's Roadmap to Net-Zero through Innovation, Policy, and Circular Economy
          </h1>
          <div className="author-block">
            <div className="author-avatar">YM</div>
            <div className="author-info">
              <span className="author-name">Yogesh Mandhani</span>
              <span className="author-role">President, AIIFA Sustainable Steel Manufacturers Association</span>
            </div>
          </div>
        </div>
      </header>

      {/* HERO IMAGE */}
      <div className="hero-image-section">
        <img
          src="https://res.cloudinary.com/dxvbzgdgw/image/upload/v1774938102/WhatsApp_Image_2026-03-31_at_11.38.44_b9jqmv.jpg"
          alt="Steel manufacturing facility with molten steel"
        />
        <div className="hero-caption">
          <span className="hero-caption-text">Electric induction furnace operations · Indian secondary steel sector</span>
        </div>
      </div>

      {/* BODY */}
      <main className="article-body">

        {/* ABSTRACT */}
        <div className="abstract-block">
          <div className="abstract-label">Abstract</div>
          <p className="abstract-text">
The global steel industry faces increasing pressure to reduce greenhouse gas emissions as nations pursue climate commitments under international frameworks such as the Paris Agreement. As the world’s second-largest steel producer, India plays a pivotal role in shaping the future trajectory of sustainable steel manufacturing. A significant portion of India’s steel output originates from the secondary steel sector, which is largely composed of small and medium-sized enterprises (SMEs) utilizing electric induction furnace technology. This paper examines the roadmap developed by the AIIFA Sustainable Steel Manufacturers Association to guide the sector toward net-zero emissions. The roadmap integrates technological innovation, renewable energy adoption, policy advocacy, circular economy principles, and human capital development. By aligning industry transformation with national climate goals and global sustainability standards, AIIFA’s framework provides a practical pathway for decarbonizing an SME-driven industrial ecosystem while maintaining economic competitiveness and industrial growth.
          </p>
        </div>

        {/* STAT CALLOUT */}
        <div className="stat-callout">
          <div className="stat-num">7–8%</div>
          <div className="stat-desc">
            of global CO₂ emissions originate from the steel industry, making decarbonization a central priority in international climate policy and industrial strategy.
          </div>
        </div>

        {/* TABLE OF CONTENTS */}
        <div className="toc-sidebar">
          <div className="toc-title">Contents</div>
          <ul className="toc-list">
            {tocItems.map((item, i) => (
              <li key={i} className="toc-item" onClick={() => scrollTo(i)}>
                <span className="toc-num">{item.num}</span>
                <span>{item.label}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* PULL QUOTE */}
        <div className="pull-quote">
          <p className="pull-quote-text">
            "AIIFA's framework provides a practical pathway for decarbonizing an SME-driven industrial ecosystem while maintaining economic competitiveness and industrial growth."
          </p>
        </div>

        {/* SECTIONS */}
        {sections.map((sec, idx) => (
          <section
            key={idx}
            id={`section-${idx}`}
            className="section"
            style={{ animationDelay: `${idx * 0.05}s` }}
          >
            <div className="section-number">Section {sec.num}</div>
            <h2 className="section-title">{sec.title}</h2>
            <div className="section-body">{sec.content}</div>
          </section>
        ))}

      </main>

      {/* FOOTER */}
      {/* <footer className="article-footer">
        <div className="footer-logo">AIIFA · Sustainable Steel Manufacturers Association · All Rights Reserved</div>
      </footer> */}
    </div>
  )
}

export default Article1